import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, Send, RefreshCw } from "lucide-react";
import { PortalLayout } from "@/components/layout/PortalLayout";
import { GlassCard } from "@/components/GlassCard";
import { StatusBadge } from "@/components/Badges";
import api from "@/lib/api";
import { goldBtnSm, glassBtn, textareaCls, labelCls } from "@/lib/constants";

function Toggle({ on, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`w-11 h-6 rounded-full transition-colors relative ${on ? "bg-[hsl(var(--pdg-gold)/0.6)]" : "bg-[hsl(var(--pdg-elevated))]"}`}>
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-foreground transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

export default function BridgeSettings() {
  const [s, setS] = useState(null);
  const [events, setEvents] = useState([]);
  const [ingest, setIngest] = useState([]);
  const [hb, setHb] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [st, ev, ig] = await Promise.all([api.get("/bridge/settings"), api.get("/admin/integration/events"), api.get("/admin/integration/ingest-log")]);
    setS(st.data); setEvents(ev.data); setIngest(ig.data);
  };
  useEffect(() => { load(); }, []);

  const update = async (patch) => {
    const { data } = await api.put("/bridge/settings", patch); setS(data); toast.success("Settings updated.");
  };
  const heartbeat = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/quantus/heartbeat"); setHb(data); toast[data.reachable ? "success" : "error"](data.reachable ? "Quantus reachable." : "Quantus unreachable (expected until configured)."); load(); }
    catch (e) { toast.error("Heartbeat failed"); } finally { setBusy(false); }
  };
  const dispatch = async () => {
    try { await api.post("/admin/integration/dispatch", { type: "deal.request", payload: { note: "manual test dispatch" } }); toast.success("Event queued & dispatched."); load(); }
    catch (e) { toast.error("Dispatch failed"); }
  };
  const retry = async (id) => { await api.post(`/admin/integration/events/${id}/retry`); load(); };

  if (!s) return <PortalLayout title="Bridge Settings"><p className="text-sm text-muted-foreground">Loading…</p></PortalLayout>;

  return (
    <PortalLayout title="Bridge Settings">
      <div className="grid gap-6 lg:grid-cols-2">
        <GlassCard className="p-6">
          <h2 className="pdg-serif text-2xl text-foreground mb-5">Automation</h2>
          {[["global_automation", "Global automation", "AI auto-classification of inbound deals via ORLA"],
            ["quantus_enabled", "Quantus integration", "Accept inbound deals from Quantus"],
            ["auto_classify", "Auto-classify", "Run ORLA triage automatically on arrival"]].map(([k, label, desc]) => (
            <div key={k} className="flex items-center justify-between py-3 border-b border-border">
              <div><div className="text-sm text-foreground">{label}</div><div className="text-xs text-muted-foreground">{desc}</div></div>
              <Toggle on={s[k]} onClick={() => update({ [k]: !s[k] })} testid={`bridge-toggle-${k}`} />
            </div>
          ))}
          <div className="mt-5">
            <label className={labelCls}>Triage notes</label>
            <textarea rows={3} className={textareaCls} defaultValue={s.triage_notes} onBlur={(e) => update({ triage_notes: e.target.value })} />
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <h2 className="pdg-serif text-2xl text-foreground mb-5">Quantus connectivity</h2>
          <div className="flex gap-3">
            <button onClick={heartbeat} disabled={busy} data-testid="quantus-heartbeat-button" className={goldBtnSm}><Activity size={13} /> {busy ? "Pinging…" : "Send heartbeat"}</button>
            <button onClick={dispatch} data-testid="quantus-dispatch-button" className={glassBtn + " h-9 px-3 text-xs"}><Send size={13} /> Dispatch test event</button>
          </div>
          {hb && (
            <div className="mt-4 text-xs text-muted-foreground">
              <div>Reachable: <span className={hb.reachable ? "text-[hsl(145_38%_66%)]" : "text-[hsl(0_45%_70%)]"}>{String(hb.reachable)}</span> &middot; HTTP {hb.http_status ?? "—"}</div>
              <div className="pdg-mono mt-1 break-all">Scheme: {hb.signature_scheme}</div>
            </div>
          )}
          <div className="mt-6 pdg-kicker mb-2">Outbound events</div>
          <div className="space-y-2 max-h-52 overflow-y-auto">
            {events.map((e) => (
              <div key={e.id} className="flex items-center justify-between text-xs p-2.5 rounded-lg border border-border">
                <span className="pdg-mono text-muted-foreground">{e.type} &middot; {e.attempt_count}x</span>
                <div className="flex items-center gap-2"><StatusBadge status={e.status} />{e.status === "failed" && <button onClick={() => retry(e.id)} className="text-[hsl(var(--pdg-gold))]"><RefreshCw size={12} /></button>}</div>
              </div>
            ))}
            {events.length === 0 && <p className="text-xs text-muted-foreground">No outbound events.</p>}
          </div>
        </GlassCard>
      </div>

      <GlassCard className="p-6 mt-6">
        <h2 className="pdg-serif text-2xl text-foreground mb-4">Inbound ingest log</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="pdg-kicker text-left border-b border-border"><th className="px-4 py-2 font-normal">Event</th><th className="px-4 py-2 font-normal">Status</th><th className="px-4 py-2 font-normal">Reason</th><th className="px-4 py-2 font-normal">Received</th></tr></thead>
            <tbody>
              {ingest.map((i) => (
                <tr key={i.id} className="border-b border-border">
                  <td className="px-4 py-2 pdg-mono text-xs text-muted-foreground">{i.event_id || "—"}</td>
                  <td className="px-4 py-2"><StatusBadge status={i.status} /></td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{i.reason}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground pdg-mono">{new Date(i.received_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {ingest.length === 0 && <p className="p-4 text-sm text-muted-foreground">No inbound webhook attempts yet.</p>}
        </div>
      </GlassCard>
    </PortalLayout>
  );
}
